import React, { ReactElement } from 'react';
import TextInputContainer from 'adapter/in/common/form/Text';
import PresetFormFieldProperties from 'domain/entity/preset/form/presetFormFieldProperties';
import { ConfigFieldType } from 'adapter/in/common/form/types';
import { FIELD_TYPE } from 'domain/entity/preset/field/fieldType';
import PresetFormField, {
    PeriodSelectorPresetFormField,
    SelectorPresetFormField,
    SELECTOR_SHOW_AS,
    TextPresetFormField,
} from 'domain/entity/preset/form/presetFormField';
import PresetFormFieldDependency from 'domain/entity/preset/form/presetFormFieldDependency';
import { ApiSelectorContainer } from 'adapter/in/common/form/Selector/ApiSelectorContainer';
import { ApiPeriodSelectorContainer } from 'adapter/in/common/form/PeriodSelector/ApiPeriodSelector';
import ApiFieldGroupContainer from 'adapter/in/common/form/FieldGroup/ApiFieldGroup';
import { DefaultValuesPeriodSelectorContainer } from 'adapter/in/common/form/PeriodSelector/DefaultValuesPeriodSelector';
import DefaultValuesFieldGroupContainer from 'adapter/in/common/form/FieldGroup/DefaultValuesFieldGroup';
import { DefaultValuesSelectorContainer } from 'adapter/in/common/form/Selector/DefaultValuesSelectorContainer';

export type FieldContainer = {
    type: string;
    key: string;
    name: string;
    label: string;
    footer: string;
    affectsTo?: string[];
    dataDependsOn?: string[];
    properties?: PresetFormFieldProperties;
};

const getFieldProperties = (field: PresetFormField) => {
    if (field instanceof SelectorPresetFormField || field instanceof PeriodSelectorPresetFormField) {
        return new PresetFormFieldProperties(undefined, undefined, field.showAs, field.defaultValues, field.pageSize);
    }
    if (field instanceof TextPresetFormField) {
        return new PresetFormFieldProperties(field.placeholder, field.maxLength);
    }
    return new PresetFormFieldProperties();
};

export const asFieldContainer = (fields: PresetFormField[], field: PresetFormField): FieldContainer => ({
    key: field.key,
    type: field.type,
    name: field.key,
    label: field.labelText,
    footer: field.footerText ?? '',
    dataDependsOn: field.dataDependsOn.map((presetFormFieldDependency) => presetFormFieldDependency.field),
    affectsTo: getFormDependencies(fields)
        .filter((fieldDependency) => fieldDependency.name === field.key)
        .map((fieldDependency) => fieldDependency.dependsOn),
    properties: getFieldProperties(field),
});

type FieldDependency = { name: string; dependsOn: string; type: string };

export const getFormDependencies = (fields: PresetFormField[]): FieldDependency[] => {
    const dependencies: FieldDependency[] = [];
    fields.forEach((field: PresetFormField) => {
        const { dataDependsOn, key, type } = field;
        dataDependsOn.forEach((item: PresetFormFieldDependency) => {
            dependencies.push({
                name: item.field,
                dependsOn: key,
                type,
            });
        });
    });
    return dependencies;
};

const mapPresetFormFieldToFieldContainer = (fields: PresetFormField[]): FieldContainer[] =>
    fields.map((field: PresetFormField) => asFieldContainer(fields, field));

export const asContainer = (field: FieldContainer, configType: ConfigFieldType): ReactElement => {
    const { name, key, label, affectsTo, dataDependsOn, footer, properties } = field;

    switch (field.type) {
        case FIELD_TYPE.PERIOD_SELECTOR:
            return (
                <ApiPeriodSelectorContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    pageSize={properties?.pageSize ?? 0}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                />
            );
        case FIELD_TYPE.TEXT:
            return (
                <TextInputContainer
                    configType={configType}
                    key={key}
                    name={name}
                    label={label}
                    properties={properties}
                    footer={footer}
                    dataDependsOn={dataDependsOn}
                />
            );
        case FIELD_TYPE.SELECTOR:
        default:
            return field.properties?.showAs === SELECTOR_SHOW_AS.TOGGLE ? (
                <ApiFieldGroupContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                    footer={footer}
                />
            ) : (
                <ApiSelectorContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    pageSize={properties?.pageSize ?? 0}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                    footer={footer}
                />
            );
    }
};

export const getFieldsContainers = (fields: PresetFormField[], configType: ConfigFieldType = ConfigFieldType.DATA): ReactElement[] =>
    mapPresetFormFieldToFieldContainer(fields).map((field) => asContainer(field, configType));

export const asDefaultValuesContainer = (field: FieldContainer, configType: ConfigFieldType): ReactElement => {
    const { name, key, label, affectsTo, footer, dataDependsOn } = field;
    const style = {};
    const properties = field.properties || new PresetFormFieldProperties();
    switch (field.type) {
        case FIELD_TYPE.TEXT:
            return (
                <TextInputContainer
                    key={key}
                    configType={configType}
                    dataDependsOn={dataDependsOn}
                    footer={footer}
                    label={label}
                    name={name}
                    properties={properties}
                />
            );
        case FIELD_TYPE.PERIOD_SELECTOR:
            return (
                <DefaultValuesPeriodSelectorContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    pageSize={properties?.pageSize ?? 0}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                    properties={properties}
                    style={style}
                />
            );
        case FIELD_TYPE.SELECTOR:
        default:
            return field.properties?.showAs === SELECTOR_SHOW_AS.TOGGLE ? (
                <DefaultValuesFieldGroupContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                    properties={properties}
                    style={style}
                    footer={footer}
                />
            ) : (
                <DefaultValuesSelectorContainer
                    key={key}
                    name={name}
                    configType={configType}
                    label={label}
                    pageSize={properties?.pageSize ?? 0}
                    affectsTo={affectsTo}
                    dataDependsOn={dataDependsOn}
                    properties={properties}
                    style={style}
                    footer={footer}
                />
            );
    }
};

export const getDefaultValuesFieldsContainers = (
    fields: PresetFormField[],
    configType: ConfigFieldType = ConfigFieldType.OPTIONS
): ReactElement[] => mapPresetFormFieldToFieldContainer(fields).map((field) => asDefaultValuesContainer(field, configType));
